import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Download, FileUp, Loader2 } from "lucide-react";
import { adminApi, downloadAdmin, errMsg, fmtDate } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Btn, PageHead, Panel } from "@/admin/ui";

const n = (v) => Number(v || 0).toLocaleString("en-IN");

function Choice({ name, value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid={testid}>
      {options.map(([v, label]) => (
        <button type="button" key={v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
          className={`px-3 py-2 text-xs border text-left transition-colors ${value === v ? "bg-black text-white border-black" : "bg-white border-zinc-300 hover:border-black"}`}>{label}</button>
      ))}
    </div>
  );
}

const Stat = ({ label, value, tone = "" }) => (
  <div className="border border-zinc-200 p-4 bg-zinc-50"><p className="label-caps text-[10px] text-zinc-500">{label}</p><p className={`font-arial font-bold text-2xl mt-1 ${tone}`}>{n(value)}</p></div>
);

const Warn = ({ children, testid }) => (
  <div className="border border-amber-300 bg-amber-50 p-4 text-sm flex gap-3" data-testid={testid}><AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" /><div className="flex-1 min-w-0">{children}</div></div>
);

const Sample = ({ rows }) => (
  <ul className="mt-2 max-h-40 overflow-y-auto text-xs font-arial bg-white border border-zinc-200 divide-y divide-zinc-100">
    {rows.map((r, i) => <li key={i} className="px-2 py-1">{r}</li>)}
  </ul>
);

function StockReview({ s, opts, setOpts }) {
  const missingTotal = s.missing_count + (opts.apply_probable ? 0 : s.probable_count || 0);
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Rows in sheet" value={s.rows} /><Stat label="Matched products" value={s.matched} tone="text-emerald-700" />
        <Stat label="New products" value={s.new_count} /><Stat label="Not in sheet" value={missingTotal} tone={missingTotal ? "text-red" : ""} />
      </div>
      <p className="text-xs text-zinc-500">Columns used: catalogue no. “{s.columns.sku}”{s.columns.stock && `, stock “${s.columns.stock}”`}{s.columns.price && `, price “${s.columns.price}”`}{s.columns.regular && `, old price “${s.columns.regular}”`}</p>
      {s.dup_count > 0 && (
        <Warn testid="review-dup-warning">
          <b>{n(s.dup_count)} catalogue numbers appear more than once in the sheet.</b> What should happen?
          <Sample rows={s.dup_sample.map((d) => `${d.sku} – rows ${d.rows.join(", ")} – ${d.values.map((v) => `stock ${v.stock ?? "-"} / ₹${v.price ?? "-"}`).join(" vs ")}`)} />
          <Choice testid="review-dup-policy" value={opts.dup_policy} onChange={(v) => setOpts({ ...opts, dup_policy: v })}
            options={[["first", "Use the first row"], ["last", "Use the last row"], ["skip", "Skip these products"]]} />
        </Warn>
      )}
      {s.probable_count > 0 && (
        <Warn testid="review-probable-warning">
          <b>{n(s.probable_count)} website products look like the same record as a sheet row, but their catalogue number differs slightly</b> (a leading zero or the barcode's last digit is missing on the website).
          <Sample rows={s.probable_sample.map((p) => `Website ${p.sku} → sheet ${p.sheet_sku} · ${p.reason} · ${p.artist ? `${p.artist} – ` : ""}${p.title}`)} />
          <label className="flex items-center gap-2 mt-3"><Checkbox checked={opts.apply_probable} onCheckedChange={(v) => setOpts({ ...opts, apply_probable: !!v })} data-testid="review-apply-probable" /> Treat these as matches: update their stock & price and correct the website catalogue number to the sheet's</label>
          {!opts.apply_probable && <p className="text-xs mt-1">If unticked, they count as “not in sheet” below.</p>}
        </Warn>
      )}
      {s.price_swapped > 0 && <p className="text-xs text-zinc-600" data-testid="review-price-note">{n(s.price_swapped)} rows have a Sale Price that is not lower than the Regular Price – for those, the Regular Price is used as the selling price and no old price is shown.</p>}
      {missingTotal > 0 && (
        <Warn testid="review-missing-warning">
          <b>{n(missingTotal)} products on the website are not in this sheet</b> ({n(s.missing_in_stock)} currently in stock). They will be marked out of stock.
          <Sample rows={s.missing_sample.map((p) => [p.sku, p.artist, p.title].filter(Boolean).join(" – "))} />
          {s.rounded_count > 0 && <p className="text-xs mt-2">{n(s.rounded_count)} of these have catalogue numbers ending in several zeros (e.g. {s.rounded_sample.slice(0, 3).map((p) => p.sku).join(", ")}) – they were probably rounded by Excel when the products were first uploaded, so they can't be matched. Fix them in Products.</p>}
          <label className="flex items-center gap-2 mt-3"><Checkbox checked={opts.mark_missing_oos} onCheckedChange={(v) => setOpts({ ...opts, mark_missing_oos: !!v })} data-testid="review-mark-oos" /> Mark these {n(missingTotal)} products out of stock</label>
        </Warn>
      )}
      {s.new_count > 0 && <p className="text-sm">{n(s.new_count)} catalogue numbers in the sheet aren't on the website yet. Download them as a “New Products” sheet below, fill in the details and use Upload products.</p>}
    </>
  );
}

function ProductsReview({ s, opts, setOpts }) {
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Rows in file" value={s.rows} /><Stat label="New products" value={s.new_count} tone="text-emerald-700" />
        <Stat label="Already on website" value={s.existing_dup_count} tone={s.existing_dup_count ? "text-red" : ""} /><Stat label="Missing cat. no." value={s.no_sku + s.invalid} />
      </div>
      {s.sample.length > 0 && <div><p className="text-xs text-zinc-500 mb-1">How the first rows will be read:</p>
        <Sample rows={s.sample.map((p) => `${p.sku} · ${p.artist || "(no artist)"} – ${p.title}${p.edition ? ` · ${p.edition}` : ""} · ${p.format} · ${p.genres.join("/") || "no genre"} · ₹${p.price} · stock ${p.stock}`)} /></div>}
      {s.file_dup_count > 0 && (
        <Warn testid="review-file-dup-warning">
          <b>{n(s.file_dup_count)} catalogue numbers appear more than once in this file.</b>
          <Sample rows={s.file_dup_sample.map((d) => `${d.sku} – rows ${d.rows.join(", ")} – ${d.titles.join(" | ")}`)} />
          <Choice testid="review-file-dup-policy" value={opts.file_dup_policy} onChange={(v) => setOpts({ ...opts, file_dup_policy: v })}
            options={[["first", "Use the first row"], ["last", "Use the last row"], ["skip", "Skip these rows"]]} />
        </Warn>
      )}
      {s.existing_dup_count > 0 && (
        <Warn testid="review-existing-dup-warning">
          <b>{n(s.existing_dup_count)} catalogue numbers already exist on the website.</b>
          <Sample rows={s.existing_dup_sample.map((d) => `${d.sku} – on site: ${d.on_site} – in file: ${d.in_file}`)} />
          <Choice testid="review-existing-policy" value={opts.existing_policy} onChange={(v) => setOpts({ ...opts, existing_policy: v })}
            options={[["skip", "Keep the website version (skip)"], ["overwrite", "Overwrite with the file"]]} />
        </Warn>
      )}
    </>
  );
}

function OrdersReview({ s, opts, setOpts }) {
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Rows in file" value={s.rows} /><Stat label="Orders found" value={s.orders} /><Stat label="New orders" value={s.new_count} tone="text-emerald-700" />
        <Stat label="Already in back office" value={s.existing_dup_count} tone={s.existing_dup_count ? "text-red" : ""} />
      </div>
      <p className="text-xs text-zinc-500">{n(s.items)} items in total. Rows with the same order number are grouped into one order. Stock is not changed and no emails are sent.{s.invalid > 0 && ` ${n(s.invalid)} rows without order number or email are ignored.`}</p>
      {s.sample.length > 0 && <div><p className="text-xs text-zinc-500 mb-1">How the first orders will be read:</p><Sample rows={s.sample} /></div>}
      {s.total_mismatch_count > 0 && <p className="text-xs text-amber-700" data-testid="review-total-mismatch">{n(s.total_mismatch_count)} orders have an Order Total that doesn't equal items + shipping (e.g. {s.total_mismatch_sample.slice(0, 3).join(", ")}). The total from the file is kept.</p>}
      {s.existing_dup_count > 0 && (
        <Warn testid="review-existing-dup-warning">
          <b>{n(s.existing_dup_count)} order numbers are already in the back office.</b> Only previously imported orders can be overwritten.
          <Sample rows={s.existing_dup_sample.map((d) => `${d.sku} – ${d.in_file}`)} />
          <Choice testid="review-existing-policy" value={opts.existing_policy} onChange={(v) => setOpts({ ...opts, existing_policy: v })}
            options={[["skip", "Keep the existing orders (skip)"], ["overwrite", "Overwrite with the file"]]} />
        </Warn>
      )}
    </>
  );
}

function DeleteReview({ s }) {
  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Will be deleted" value={s.matched} tone="text-red" /><Stat label="Not found" value={s.not_found_count} /><Stat label="Repeated in file" value={s.file_dup_count} />
      </div>
      {s.matched > 0 && <div><p className="text-xs text-zinc-500">Products that will be deleted{s.matched > 50 && " (first 50)"}:</p><Sample rows={s.matched_sample.map((p) => `${p.sku} – ${p.artist} – ${p.title}`)} /></div>}
      {s.not_found_count > 0 && <div><p className="text-xs text-zinc-500">Not on the website (ignored):</p><Sample rows={s.not_found_sample} /></div>}
      {s.file_dup_count > 0 && <p className="text-xs text-zinc-500">{n(s.file_dup_count)} repeated catalogue numbers in the sheet will only be deleted once.</p>}
    </>
  );
}

const RESULT_LABELS = {
  updated: "Products updated", matched: "Matched", catalogue_numbers_corrected: "Catalogue numbers corrected", skipped_duplicates: "Skipped (duplicates)", marked_out_of_stock: "Marked out of stock",
  new_products: "New products found", inserted: "Products added", overwritten: "Products overwritten", skipped_file_duplicates: "Skipped (file duplicates)",
  skipped_existing: "Skipped (already on site)", deleted: "Products deleted", orders_added: "Orders added", orders_overwritten: "Orders overwritten",
};

function canConfirm(rawKind, s, o) {
  const kind = rawKind.replace("gear_", "");
  if (kind === "stock") return (!s.dup_count || o.dup_policy) && (s.matched > 0 || o.mark_missing_oos);
  if (kind === "products") return (!s.file_dup_count || o.file_dup_policy) && (!s.existing_dup_count || o.existing_policy) && s.unique > 0;
  if (kind === "orders") return (!s.existing_dup_count || o.existing_policy) && s.orders > 0;
  return s.matched > 0;
}

export function JobRunner({ kind, confirmLabel }) {
  const [job, setJob] = useState(null);
  const [opts, setOpts] = useState({ dup_policy: "", file_dup_policy: "", existing_policy: "", mark_missing_oos: false, apply_probable: true });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => adminApi.get("/jobs", { params: { kind } }).then((r) => setHistory(r.data));
  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!job || !["analyzing", "applying"].includes(job.status)) return undefined;
    const t = setTimeout(() => adminApi.get(`/jobs/${job.id}`).then((r) => { setJob(r.data); if (r.data.status === "done") loadHistory(); }), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await adminApi.post("/jobs/upload", fd);
      setOpts({ dup_policy: "", file_dup_policy: "", existing_policy: "", mark_missing_oos: false, apply_probable: true });
      setJob({ id: r.data.id, status: "analyzing", progress: 0, stage_text: "Uploaded", filename: file.name });
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const act = async (path, body) => {
    setBusy(true);
    try {
      await adminApi.post(`/jobs/${job.id}/${path}`, body);
      setJob((await adminApi.get(`/jobs/${job.id}`)).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const s = job?.summary;
  const Review = { stock: StockReview, products: ProductsReview, delete: DeleteReview, orders: OrdersReview }[kind.replace("gear_", "")];
  return (
    <div className="space-y-6">
      <Panel>
        <label className="flex flex-col items-center justify-center border-2 border-dashed border-zinc-300 hover:border-black transition-colors p-10 cursor-pointer text-center" data-testid="job-dropzone">
          <FileUp className="w-8 h-8 text-red" strokeWidth={1.5} />
          <span className="mt-3 font-bold text-sm">Choose a .csv or .xlsx file</span>
          <span className="text-xs text-zinc-500 mt-1">Nothing changes until you review and confirm.</span>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" disabled={busy || ["analyzing", "applying"].includes(job?.status)} onChange={(e) => upload(e.target.files[0])} data-testid="job-file-input" />
        </label>
      </Panel>
      {job && (
        <Panel className="space-y-5" data-testid="job-panel">
          <div className="flex justify-between gap-3 items-center">
            <p className="font-bold text-sm truncate">{job.filename}</p>
            <span className="label-caps text-[10px] text-zinc-500" data-testid="job-status">{job.status}</span>
          </div>
          {["analyzing", "applying"].includes(job.status) && (
            <div data-testid="job-progress">
              <p className="text-sm flex items-center gap-2 mb-2"><Loader2 className="w-4 h-4 animate-spin" /> {job.stage_text} {job.processed ? `· ${n(job.processed)} done` : ""}</p>
              <Progress value={job.progress || 0} className="h-2 rounded-none" />
              <p className="text-xs text-zinc-500 mt-2">Runs in the background – you can leave this page and come back.</p>
            </div>
          )}
          {job.status === "failed" && <p className="text-sm text-red" data-testid="job-error">{job.error}</p>}
          {job.status === "cancelled" && <p className="text-sm text-zinc-500">Cancelled – nothing was changed.</p>}
          {job.status === "review" && s && (
            <>
              <Review s={s} opts={opts} setOpts={setOpts} />
              <div className="flex flex-wrap gap-2 pt-2">
                <Btn variant="red" disabled={busy || !canConfirm(kind, s, opts)} onClick={() => act("confirm", opts)} data-testid="job-confirm">{confirmLabel}</Btn>
                <Btn variant="outline" disabled={busy} onClick={() => act("cancel")} data-testid="job-cancel">Cancel</Btn>
                {kind === "stock" && s.new_count > 0 && <Btn variant="outline" onClick={() => downloadAdmin(`/jobs/${job.id}/new-products`, "new-products.csv")} data-testid="job-download-new"><Download className="w-4 h-4" /> New Products sheet</Btn>}
              </div>
            </>
          )}
          {job.status === "done" && job.result && (
            <div data-testid="job-result">
              <p className="flex items-center gap-2 font-bold text-emerald-700"><CheckCircle2 className="w-5 h-5" /> Finished</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">{Object.entries(job.result).map(([k, v]) => <Stat key={k} label={RESULT_LABELS[k] || k} value={v} />)}</div>
              {kind === "stock" && job.result.new_products > 0 && <Btn variant="outline" className="mt-4" onClick={() => downloadAdmin(`/jobs/${job.id}/new-products`, "new-products.csv")} data-testid="job-download-new-done"><Download className="w-4 h-4" /> Download New Products sheet</Btn>}
            </div>
          )}
        </Panel>
      )}
      {history.length > 0 && (
        <Panel>
          <p className="label-caps text-[10px] text-zinc-500 mb-3">Recent uploads</p>
          <ul className="text-sm divide-y divide-zinc-100">
            {history.map((h) => (
              <li key={h.id} className="py-2 flex justify-between gap-3">
                <button className="truncate text-left hover:text-red" onClick={() => adminApi.get(`/jobs/${h.id}`).then((r) => setJob(r.data))}>{h.filename}</button>
                <span className="text-xs text-zinc-500 shrink-0">{h.status} · {fmtDate(h.created_at)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

export function StockPage() {
  return (
    <div className="max-w-4xl">
      <PageHead title="Stock & price sheet" desc="Upload your weekly stock sheet (CSV or Excel). Rows are matched on catalogue number; stock and prices (₹) are updated in one go in the background." />
      <JobRunner kind="stock" confirmLabel="Apply stock & prices" />
    </div>
  );
}

export function ImportPage() {
  return (
    <div className="max-w-4xl">
      <PageHead title="Upload products" desc="Import products from a WooCommerce product export (CSV) or an Excel/CSV sheet in the Upload products format.">
        <Btn variant="outline" onClick={() => downloadAdmin("/jobs/template/products", "upload-products-template.csv")} data-testid="import-template-download"><Download className="w-4 h-4" /> Template</Btn>
      </PageHead>
      <JobRunner kind="products" confirmLabel="Import products" />
    </div>
  );
}

export function OrdersImportPage() {
  return (
    <div className="max-w-4xl">
      <PageHead title="Import old orders" desc="Upload orders from your previous website (CSV or Excel), one row per item. Rows with the same Order Number become one order.">
        <Btn variant="outline" onClick={() => downloadAdmin("/jobs/template/orders", "import-orders-template.csv")} data-testid="orders-template-download"><Download className="w-4 h-4" /> Template</Btn>
      </PageHead>
      <JobRunner kind="orders" confirmLabel="Import orders" />
    </div>
  );
}

export function GearStockPage() {
  return (
    <div className="max-w-4xl">
      <PageHead title="Gear stock sheet" desc="Stock & price sheet for turntables and accessories only. Matched on SKU; records are never touched, and only gear missing from this sheet can be marked out of stock." />
      <JobRunner kind="gear_stock" confirmLabel="Apply gear stock & prices" />
    </div>
  );
}

export function GearImportPage() {
  return (
    <div className="max-w-4xl">
      <PageHead title="Upload turntables & accessories" desc="Import a WooCommerce product export of turntables and accessories. The category (e.g. Turntables, Storage & Display) becomes the sub-section in the menu; Brands or the first tag becomes the brand." />
      <JobRunner kind="gear_products" confirmLabel="Import gear" />
    </div>
  );
}

export function DeletePage() {
  return (
    <div className="max-w-4xl">
      <PageHead title="Bulk delete by sheet" desc="Upload a sheet with a 'Catalogue Number' column. You'll see exactly what will be deleted before confirming. To delete a few products, select them on the Products page." />
      <JobRunner kind="delete" confirmLabel="Delete these products" />
    </div>
  );
}
